import React, { useState, useEffect, useCallback } from "react";
import {
    Container,
    Segment,
    Header,
    Button,
    Table,
    Icon,
    Dropdown,
    Loader,
    Message,
} from "semantic-ui-react";
import { useHistory } from "react-router-dom";
import { GetRecentLogins, RecentLoginEntry } from "../Utils/UserAPI";

const DEFAULT_DAYS = 30;
const DAY_OPTIONS = [7, 14, 30, 60, 90, 180, 365];

export default function UserLogins() {
    const history = useHistory();
    const [days, setDays] = useState(DEFAULT_DAYS);
    const [logins, setLogins] = useState<RecentLoginEntry[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);

    const loadLogins = useCallback((requestedDays: number) => {
        setLoading(true);
        setError(null);
        GetRecentLogins(requestedDays, (statusCode, data) => {
            setLoading(false);
            if (statusCode === 200) {
                setLogins(data);
            } else if (statusCode === 403) {
                setError("You do not have permission to view recent logins.");
            } else {
                setError("Failed to load recent logins. Please try again.");
            }
        });
    }, []);

    useEffect(() => {
        loadLogins(days);
    }, [days, loadLogins]);

    return (
        <Container>
            <Header as="h2">
                <Icon name="sign in" />
                <Header.Content>
                    Recent Logins
                    <Header.Subheader>
                        Admin-only feed of member logins
                    </Header.Subheader>
                </Header.Content>
            </Header>

            <div style={{ marginBottom: "1rem" }}>
                <Button onClick={() => history.push("/users")}>
                    <Icon name="arrow left" />
                    Back to User List
                </Button>
            </div>

            <Segment>
                <div style={{ display: "flex", alignItems: "center", gap: "0.5rem", marginBottom: "1rem" }}>
                    <label>Show logins from the last</label>
                    <Dropdown
                        selection
                        compact
                        options={DAY_OPTIONS.map((d) => ({ key: d, text: `${d} days`, value: d }))}
                        value={days}
                        onChange={(e, { value }) => setDays(value as number)}
                    />
                </div>

                {loading ? (
                    <Loader active size="large">
                        Loading logins...
                    </Loader>
                ) : error ? (
                    <Message negative>
                        <Message.Header>Error</Message.Header>
                        <p>{error}</p>
                    </Message>
                ) : logins.length === 0 ? (
                    <Message info>
                        <Message.Header>No Logins Found</Message.Header>
                        <p>No member logins have been recorded in the last {days} days.</p>
                    </Message>
                ) : (
                    <Table celled>
                        <Table.Header>
                            <Table.Row>
                                <Table.HeaderCell>Date/Time</Table.HeaderCell>
                                <Table.HeaderCell>User</Table.HeaderCell>
                                <Table.HeaderCell>IP Address</Table.HeaderCell>
                            </Table.Row>
                        </Table.Header>
                        <Table.Body>
                            {logins.map((entry) => (
                                <Table.Row key={entry.id}>
                                    <Table.Cell>
                                        {new Date(entry.time).toLocaleString()}
                                    </Table.Cell>
                                    <Table.Cell>
                                        <a href={`/users/${entry.user_id}`}>
                                            {entry.user_first_name} {entry.user_last_name}
                                        </a>
                                    </Table.Cell>
                                    <Table.Cell>{entry.ip_address}</Table.Cell>
                                </Table.Row>
                            ))}
                        </Table.Body>
                    </Table>
                )}
            </Segment>
        </Container>
    );
}
